import React, { useState, useEffect } from 'react';
import {
  SimpleForm,
  TextInput,
  NumberInput,
  DateInput,
  SelectInput,
  BooleanInput,
  required,
  useNotify,
  useRefresh,
  useDataProvider,
  useTranslate,
  Toolbar,
  SaveButton,
} from 'react-admin';
import { Dialog, DialogTitle, DialogContent, IconButton, Grid } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';

const IncidentToolbar = () => (
  <Toolbar
    sx={{
      display: 'flex',
      justifyContent: 'flex-end',
      padding: '16px 24px',
      borderTop: '1px solid #e0e0e0',
    }}
  >
    <SaveButton />
  </Toolbar>
);

interface IncidentDialogProps {
  open: boolean;
  onClose: () => void;
  equipmentId: number;
  recordId?: number;
  isEdit?: boolean;
}

export const IncidentDialog: React.FC<IncidentDialogProps> = ({
  open,
  onClose,
  equipmentId,
  recordId,
  isEdit = false,
}) => {
  const translate = useTranslate();
  const notify = useNotify();
  const refresh = useRefresh();
  const dataProvider = useDataProvider();
  const [record, setRecord] = useState(null);
  const [drivers, setDrivers] = useState([]);

  const incidentTypes = [
    { id: 'ACCIDENT', name: 'Accident' },
    { id: 'BREAKDOWN', name: 'Breakdown' },
    { id: 'DAMAGE', name: 'Damage' },
    { id: 'THEFT', name: 'Theft' },
    { id: 'VANDALISM', name: 'Vandalism' },
    { id: 'FIRE', name: 'Fire' },
    { id: 'FLOOD', name: 'Flood' },
    { id: 'OTHER', name: 'Other' },
  ];

  // Load drivers when dialog opens
  useEffect(() => {
    if (open) {
      dataProvider
        .getList('drivers', {
          pagination: { page: 1, perPage: 1000 },
          sort: { field: 'id', order: 'ASC' },
          filter: {},
        })
        .then(({ data }) => {
          const driverChoices = data.map(driver => ({
            id: driver.id,
            name: driver.user?.name || `Driver ${driver.id}`,
          }));
          setDrivers(driverChoices);
        })
        .catch(error => {
          console.error('Error loading drivers:', error);
          notify('Error loading drivers', { type: 'error' });
        });
    }
  }, [open, dataProvider, notify]);

  // Load record for edit mode
  useEffect(() => {
    if (isEdit && recordId && open) {
      true;
      dataProvider
        .getOne('equipment-incidents', { id: recordId })
        .then(({ data }) => {
          setRecord(data);
        })
        .catch(error => {
          console.error('Error loading incident record:', error);
          notify('Error loading incident record', { type: 'error' });
        })
        .finally(() => {
          false;
        });
    } else if (!isEdit) {
      setRecord(null);
    }
  }, [isEdit, recordId, open, dataProvider, notify]);

  const handleSubmit = async (data: any) => {
    true;
    try {
      const formattedData = {
        ...data,
        equipmentId: Number(equipmentId),
      };

      if (isEdit && recordId) {
        await dataProvider.update('equipment-incidents', {
          id: recordId,
          data: formattedData,
        });
        notify('Incident record updated successfully', { type: 'success' });
      } else {
        await dataProvider.create('equipment-incidents', {
          data: formattedData,
        });
        notify('Incident record created successfully', { type: 'success' });
      }

      refresh();
      onClose();
    } catch (error) {
      console.error('Incident submission error:', error);
      notify(
        `Error ${isEdit ? 'updating' : 'creating'} incident record: ${
          error.message || 'Unknown error'
        }`,
        { type: 'error' }
      );
    } finally {
      false;
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        {isEdit
          ? translate('resources.equipment_incidents.edit.title')
          : translate('resources.equipment_incidents.create.title')}
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <SimpleForm
          onSubmit={handleSubmit}
          record={
            record || {
              incidentDate: new Date().toISOString().split('T')[0],
            }
          }
          toolbar={<IncidentToolbar />}
        >
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} md={6}>
              <DateInput
                source="incidentDate"
                validate={[required()]}
                fullWidth
                label="Incident Date"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <SelectInput
                source="incidentType"
                choices={incidentTypes}
                validate={[required()]}
                fullWidth
                label="Incident Type"
              />
            </Grid>
            <Grid item xs={12}>
              <TextInput
                source="description"
                validate={[required()]}
                fullWidth
                multiline
                rows={4}
                label="Description"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <SelectInput
                source="driverId"
                choices={drivers}
                fullWidth
                label="Driver (if applicable)"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <NumberInput source="repairCost" fullWidth label="Repair Cost ($)" />
            </Grid>
            <Grid item xs={12} md={6}>
              <BooleanInput source="resolved" label="Resolved" />
            </Grid>
            <Grid item xs={12}>
              <TextInput source="notes" fullWidth multiline rows={3} label="Additional Notes" />
            </Grid>
          </Grid>
        </SimpleForm>
      </DialogContent>
    </Dialog>
  );
};
